import React, { useState, useEffect } from 'react';
import {
  Building2,
  User,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Loader2,
  RefreshCw,
  Clock,
  Shield,
  ShieldCheck,
  Send,
  Phone,
  Settings as SettingsIcon
} from 'lucide-react';
import supabase from '../../lib/supabaseClient';
import { useAuth } from '../../context/AuthContext.jsx';
import { formatEINForTwilio, formatEINForDisplay, formatPhoneForTwilio } from '../../lib/a2pFormat';


const A2PRegistration = ({ onComplete, onNavigateToPhoneNumbers }) => {
  // EIN/SSN/phone formatting now lives in src/lib/a2pFormat.js (shared with the
  // onboarding A2P capture step so the two never drift on Twilio's strict shapes).

  // Carrier registration is now fully automated (onboarding capture + the
  // refresh-a2p-status cron auto-advancing brand → campaign → number). So the
  // default experience here is a READ-ONLY status view — no manual forms. The
  // legacy step-by-step wizard is preserved behind an admin toggle as an escape
  // hatch for when automation needs a manual nudge.
  const { user } = useAuth();
  const isGlobalAdmin = user?.role === 'global_admin';
  const [showAdvancedWizard, setShowAdvancedWizard] = useState(false);

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [checkingStatus, setCheckingStatus] = useState(false);

  // Track what's been created and their statuses
  const [registrationStatus, setRegistrationStatus] = useState({
    subaccount: { created: false, status: null },
    customerProfile: { created: false, status: null, sid: null },
    brand: { created: false, status: null, sid: null },
    campaign: { created: false, status: null, sid: null }
  });

  // Form data for all steps
  const [formData, setFormData] = useState({
    // Step 1: Business + Authorized Rep (creates Customer Profile)
    business_name: '',
    business_type: 'CORPORATION',
    business_registration_number: '',
    business_industry: '',
    street: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    email: '',
    phone: '',
    website: '',
    auth_rep_first_name: '',
    auth_rep_last_name: '',
    auth_rep_email: '',
    auth_rep_phone: '',
    auth_rep_business_title: '',
    auth_rep_job_position: 'Director',

    // Step 2: Brand Registration (after customer profile approved)
    company_name: '',
    company_type: 'CORPORATION',
    ein: '',
    vertical: 'REAL_ESTATE',

  // Step 3: Campaign (after brand approved)
  campaign_description: '',
  message_flow: '',
  us_app_to_person_usecase: 'MIXED',
  message_samples: [],
  daily_message_volume: '1000',
  opt_in_preset: '',
  compliance_opt_in: false,
  compliance_opt_out: false,
  compliance_tcpa: false
});

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const addMessageSample = () => {
    if (formData.message_samples.length < 5) {
      setFormData(prev => ({
        ...prev,
        message_samples: [...prev.message_samples, '']
      }));
    }
  };

  const updateMessageSample = (index, value) => {
    const newSamples = [...formData.message_samples];
    newSamples[index] = value;
    setFormData(prev => ({ ...prev, message_samples: newSamples }));
  };

  const removeMessageSample = (index) => {
    if (formData.message_samples.length > 1) {
      setFormData(prev => ({
        ...prev,
        message_samples: prev.message_samples.filter((_, i) => i !== index)
      }));
    }
  };

  const fetchTenantIndustry = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.log('No user found');
      return;
    }
    
    // tenant_id is in app_metadata, not user_metadata
    const tenantId = user.app_metadata?.tenant_id;
    
    console.log('Tenant ID:', tenantId);
    
    if (!tenantId) {
      console.log('No tenant_id in app_metadata');
      return;
    }
    
    const { data, error } = await supabase
      .from('tenants')
      .select('industry')
      .eq('id', tenantId)
      .single();
    
    if (error) {
      console.error('Error fetching tenant:', error);
      return;
    }
    
    if (data?.industry) {
      console.log('Tenant industry:', data.industry);
      setFormData(prev => ({ ...prev, business_industry: data.industry }));
    }
  } catch (err) {
    console.error('Error:', err);
  }
};

useEffect(() => {
  checkA2PStatus();
  fetchTenantIndustry();
}, []);

  const checkA2PStatus = async () => {
  setCheckingStatus(true);
  try {
    const { data, error } = await supabase.functions.invoke('get-a2p-status');
    
    if (error) throw error;

    if (data && data.success) {
      // Update registration status based on what exists
      const newStatus = { ...registrationStatus };

      // Check customer profile status first
      if (data.customerProfile) {
        newStatus.customerProfile = {
          created: true,
          status: data.customerProfile.status,
          sid: data.customerProfile.twilio_customer_profile_sid
        };
        console.log('Customer Profile Status:', data.customerProfile.status);
      }

      if (data.brand) {
        newStatus.brand = {
          created: true,
          status: data.brand.status,
          sid: data.brand.twilio_brand_sid,
          failure_reason: data.brand.failure_reason || null
        };
      }

if (data.campaigns && data.campaigns.length > 0) {
  newStatus.campaign = {
    created: true,
    status: data.campaigns[0].status,
    sid: data.campaigns[0].twilio_campaign_sid,
    failure_reason: data.campaigns[0].failure_reason || null
  };
}

      setRegistrationStatus(newStatus);

      // UPDATED LOGIC - Determine which step to show based on what's completed
      if (data.campaigns && data.campaigns.length > 0) {
        // Campaign exists - go to completion screen
        setCurrentStep(4);
     } else if (data.brand && (data.brand.status === 'VERIFIED' || data.brand.status === 'APPROVED')) {
        // Brand is approved - go to campaign setup
        setCurrentStep(3);
        setSuccess('Brand approved! You can now create your campaign.');
      } else if (data.brand && data.brand.status === 'IN_REVIEW') {
        // Brand is in review - stay on brand waiting screen
        setCurrentStep(2.5);
      } else if (data.brand) {
        // Brand exists but not approved yet - go to brand waiting screen
        setCurrentStep(2.5);
      } else if (data.customerProfile && data.customerProfile.status === 'twilio-approved') {
        // Customer profile approved but no brand - go to brand registration (step 2)
        setCurrentStep(2);
      } else if (data.customerProfile) {
        // Customer profile exists but not approved - stay on step 2 (waiting screen)
        setCurrentStep(2);
      } else {
        // Nothing exists - start from step 1
        setCurrentStep(1);
      }
    }
  } catch (err) {
    console.error('Error checking A2P status:', err);
    setError('Failed to check registration status');
  } finally {
    setCheckingStatus(false);
  }
};

  const handleStep1Submit = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Step 1.1: Ensure subaccount exists
      const { data: subaccountData, error: subaccountError } = await supabase.functions.invoke('create-twilio-subaccount');
      
      if (subaccountError) {
        throw new Error('Failed to create subaccount: ' + subaccountError.message);
      }

      if (!subaccountData.success) {
        throw new Error(subaccountData.message || 'Subaccount creation failed');
      }

      // Step 1.2: Create customer profile with business + authorized rep data
      const customerProfilePayload = {
        business_name: formData.business_name,
        business_type: formData.business_type,
        business_registration_number: formData.business_registration_number,
        business_industry: formData.business_industry,
        street: formData.street,
        city: formData.city,
        state: formData.state,
        postal_code: formData.postal_code,
        country: formData.country,
        email: formData.email,
        phone: formatPhoneForTwilio(formData.phone),
        website: formData.website,
        auth_rep_first_name: formData.auth_rep_first_name,
        auth_rep_last_name: formData.auth_rep_last_name,
        auth_rep_email: formData.auth_rep_email,
        auth_rep_phone: formatPhoneForTwilio(formData.auth_rep_phone),
        auth_rep_business_title: formData.auth_rep_business_title,
        auth_rep_job_position: formData.auth_rep_job_position
      };

      const { data: profileData, error: profileError } = await supabase.functions.invoke('create-customer-profile', {
        body: customerProfilePayload
      });

      if (profileError) {
        throw new Error('Failed to create customer profile: ' + profileError.message);
      }

      if (!profileData.success) {
        throw new Error(profileData.message || 'Customer profile creation failed');
      }

      setRegistrationStatus(prev => ({
        ...prev,
        subaccount: { created: true, status: 'active' },
        customerProfile: { 
          created: true, 
          status: 'pending-review',
          sid: profileData.profile.twilio_customer_profile_sid 
        }
      }));

      setSuccess('Customer Profile submitted successfully! Waiting for Twilio approval...');
      setCurrentStep(2); // Move to status waiting screen

    } catch (err) {
      console.error('Step 1 error:', err);
      setError(err.message || 'Failed to submit customer profile');
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Submit = async () => {
  setLoading(true);
  setError('');
  setSuccess('');

  try {
    // Fetch the customer profile data from the database since formData is cleared
    const { data: { user } } = await supabase.auth.getUser();
    const tenantId = user.app_metadata?.tenant_id;

    const { data: customerProfile, error: fetchError } = await supabase
      .from('customer_profiles')
      .select('*')
      .eq('tenant_id', tenantId)
      .single();

    if (fetchError || !customerProfile) {
      throw new Error('Could not fetch customer profile data');
    }

    console.log('✅ Fetched customer profile:', customerProfile);

    // Build brand payload from database data
    const brandPayload = {
      company_name: customerProfile.business_name,
      company_type: customerProfile.business_type,
      ein: customerProfile.business_registration_number,
      phone: customerProfile.phone,
      street: customerProfile.street,
      city: customerProfile.city,
      state: customerProfile.state,
      postal_code: customerProfile.postal_code,
      country: customerProfile.country,
      email: customerProfile.email,
      website: customerProfile.website || '',
      vertical: customerProfile.business_industry
    };

    console.log('Brand Payload from DB:', brandPayload);

    const { data: brandData, error: brandError } = await supabase.functions.invoke('create-a2p-brand', {
      body: brandPayload
    });

    if (brandError) {
      throw new Error('Failed to create brand: ' + brandError.message);
    }

    if (!brandData.success) {
      throw new Error(brandData.message || 'Brand creation failed');
    }

    setRegistrationStatus(prev => ({
      ...prev,
      brand: { 
        created: true, 
        status: brandData.brand.status,
        sid: brandData.brand.twilio_brand_sid 
      }
    }));

    setSuccess('A2P Brand registered successfully! Waiting for TCR approval...');
    setCurrentStep(2.5);

  } catch (err) {
    console.error('Step 2 error:', err);
    setError(err.message || 'Failed to create brand');
  } finally {
    setLoading(false);
  }
};

  const handleStep3Submit = async () => {
  setLoading(true);
  setError('');
  setSuccess('');

  try {
    // Simplified payload - only 3-4 fields
    const campaignPayload = {
      campaignName: formData.campaign_description,
      volumeTier: formData.daily_message_volume || 'low_volume_standard',
      optInMethod: formData.message_flow
    };

    console.log('Campaign Payload:', campaignPayload);

    const { data: campaignData, error: campaignError } = await supabase.functions.invoke('create-a2p-campaign', {
      body: campaignPayload
    });

    if (campaignError) {
      throw new Error('Failed to create campaign: ' + campaignError.message);
    }

    if (!campaignData.success) {
      throw new Error(campaignData.error || campaignData.message || 'Campaign creation failed');
    }

    setRegistrationStatus(prev => ({
      ...prev,
      campaign: { 
        created: true, 
        status: campaignData.status || 'pending',
        sid: campaignData.campaignSid
      }
    }));

    setSuccess('Campaign registered successfully! Carrier review started.');
    setCurrentStep(4);

  } catch (err) {
    console.error('Step 3 error:', err);
    setError(err.message || 'Failed to create campaign');
  } finally {
    setLoading(false);
  }
};

const canProceedStep1 = () => {
  return formData.business_name && formData.business_type && formData.street && 
         formData.city && formData.state && formData.postal_code && formData.email && 
         formData.phone && formData.website && formData.auth_rep_first_name && formData.auth_rep_last_name &&
         formData.auth_rep_email && formData.auth_rep_phone && formData.auth_rep_business_title;
};

const canProceedStep3 = () => {
  if (!formData.campaign_description || formData.campaign_description.trim().length < 5) {
    return false;
  }
  
  if (!formData.message_flow || formData.message_flow.trim().length < 10) {
    return false;
  }
  
  if (formData.message_samples.length === 0) {
    return false;
  }
  
  if (!formData.compliance_opt_in || !formData.compliance_opt_out || !formData.compliance_tcpa) {
    return false;
  }
  
  return true;
};

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[
        { num: 1, label: 'Business Profile', icon: Building2 },
        { num: 2, label: 'Approval Wait', icon: Clock },
        { num: 3, label: 'Campaign Setup', icon: MessageSquare },
        { num: 4, label: 'Complete', icon: CheckCircle }
      ].map((step, index) => (
        <React.Fragment key={step.num}>
          <div className={`flex flex-col items-center ${currentStep >= step.num ? 'opacity-100' : 'opacity-40'}`}>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              currentStep > step.num ? 'bg-green-500 text-white' :
              currentStep === step.num ? 'bg-blue-500 text-white' :
              'bg-gray-200 text-gray-500'
            }`}>
              {currentStep > step.num ? (
                <CheckCircle className="w-6 h-6" />
              ) : (
                <step.icon className="w-6 h-6" />
              )}
            </div>
            <span className="text-xs mt-2 text-center max-w-[80px]">{step.label}</span>
          </div>
          {index < 3 && (
            <div className={`w-16 h-1 mx-2 ${currentStep > step.num ? 'bg-green-500' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // ── Read-only status view (default) ──────────────────────────────────────
  // Derives a 4-stage pipeline purely from what get-a2p-status already returned
  // into registrationStatus. No forms, no submit paths — display only.
  const deriveStages = () => {
    const p = registrationStatus.customerProfile;
    const b = registrationStatus.brand;
    const c = registrationStatus.campaign;

    const isFail = (s) => typeof s === 'string' && /reject|fail/i.test(s);

    // Business Profile
    let profileState = 'pending';
    if (p?.created) {
      if (p.status === 'twilio-approved') profileState = 'complete';
      else if (isFail(p.status)) profileState = 'failed';
      else profileState = 'active';
    }

    // Carrier Brand
    let brandState = 'pending';
    if (b?.created) {
      if (['APPROVED', 'VERIFIED'].includes(b.status)) brandState = 'complete';
      else if (isFail(b.status)) brandState = 'failed';
      else brandState = 'active';
    }

    // Messaging Campaign
    let campaignState = 'pending';
    if (c?.created) {
      if (c.status === 'VERIFIED') campaignState = 'complete';
      else if (isFail(c.status)) campaignState = 'failed';
      else campaignState = 'active';
    }

    // Ready to send — only once the campaign is verified
    const readyState = campaignState === 'complete' ? 'complete' : 'pending';

    return [
      {
        key: 'profile',
        icon: Building2,
        title: 'Business Profile',
        state: profileState,
        desc: {
          complete: 'Your business identity has been verified by the carriers.',
          active: 'Submitted to the carriers — under review (typically 5–10 days).',
          pending: 'Preparing your business identity for submission.',
          failed: registrationStatus.customerProfile?.failure_reason || 'The carriers could not verify your business details.'
        }[profileState]
      },
      {
        key: 'brand',
        icon: ShieldCheck,
        title: 'Carrier Brand Registration',
        state: brandState,
        desc: {
          complete: 'Your brand is registered and approved.',
          active: 'Registering your brand with the carriers (usually 1–3 hours).',
          pending: 'Starts automatically once your business profile is approved.',
          failed: registrationStatus.brand?.failure_reason || 'Brand registration needs attention.'
        }[brandState]
      },
      {
        key: 'campaign',
        icon: MessageSquare,
        title: 'Messaging Campaign',
        state: campaignState,
        desc: {
          complete: 'Your messaging campaign is approved.',
          active: 'Submitting your messaging campaign for carrier review (1–3 days).',
          pending: 'Starts automatically once your brand is approved.',
          failed: registrationStatus.campaign?.failure_reason || 'Campaign registration needs attention.'
        }[campaignState]
      },
      {
        key: 'ready',
        icon: Send,
        title: 'Ready to Send',
        state: readyState,
        desc: {
          complete: 'Everything is approved. Your number can send messages to leads.',
          pending: 'Unlocks automatically once your campaign is approved.'
        }[readyState]
      }
    ];
  };

  const renderStatusView = () => {
    const stages = deriveStages();
    const allComplete = stages.every((s) => s.state === 'complete');
    const anyFailed = stages.some((s) => s.state === 'failed');

    const stateStyles = {
      complete: {
        ring: 'bg-green-500 text-white',
        pill: 'bg-green-100 text-green-700',
        pillText: 'Complete',
        Icon: CheckCircle
      },
      active: {
        ring: 'bg-blue-500 text-white',
        pill: 'bg-blue-100 text-blue-700',
        pillText: 'In progress',
        Icon: Loader2
      },
      pending: {
        ring: 'bg-gray-200 text-gray-400',
        pill: 'bg-gray-100 text-gray-500',
        pillText: 'Waiting',
        Icon: Clock
      },
      failed: {
        ring: 'bg-red-500 text-white',
        pill: 'bg-red-100 text-red-700',
        pillText: 'Needs attention',
        Icon: AlertCircle
      }
    };

    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="px-8 pt-8 pb-6 border-b border-gray-100">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">SMS Carrier Registration</h2>
                  <p className="text-sm text-gray-500 mt-0.5">Required by mobile carriers before SurFox can text your leads</p>
                </div>
              </div>
              <button
                onClick={checkA2PStatus}
                disabled={checkingStatus}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1.5 disabled:opacity-50 flex-shrink-0"
              >
                <RefreshCw className={`w-4 h-4 ${checkingStatus ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {/* Reassurance banner */}
            <div className={`mt-5 rounded-lg p-4 border ${
              allComplete
                ? 'bg-green-50 border-green-200'
                : anyFailed
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-blue-50 border-blue-200'
            }`}>
              {allComplete ? (
                <p className="text-sm text-green-800">
                  <strong>You're fully registered.</strong> Nothing more to do here — your number is cleared to message leads.
                </p>
              ) : anyFailed ? (
                <p className="text-sm text-amber-800">
                  <strong>One step needs attention.</strong> Our team is automatically notified and works these directly with the carriers. You don't need to do anything — reach out to support if you'd like an update.
                </p>
              ) : (
                <p className="text-sm text-blue-800">
                  <strong>SurFox is handling this for you automatically.</strong> We register your business with the mobile carriers in the background. No action needed — this page just shows you where things stand.
                </p>
              )}
            </div>
          </div>

          {/* Stage tracker */}
          <div className="px-8 py-6">
            <div className="relative">
              {stages.map((stage, i) => {
                const styles = stateStyles[stage.state];
                const RingIcon = styles.Icon;
                const isLast = i === stages.length - 1;
                return (
                  <div key={stage.key} className="flex gap-4 pb-6 last:pb-0 relative">
                    {/* Connector line */}
                    {!isLast && (
                      <div className={`absolute left-[19px] top-10 bottom-0 w-0.5 ${
                        stage.state === 'complete' ? 'bg-green-300' : 'bg-gray-200'
                      }`} />
                    )}
                    {/* Ring */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${styles.ring}`}>
                      <RingIcon className={`w-5 h-5 ${stage.state === 'active' ? 'animate-spin' : ''}`} />
                    </div>
                    {/* Body */}
                    <div className="flex-1 pt-1">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <stage.icon className="w-4 h-4 text-gray-400" />
                          <p className="font-semibold text-gray-900">{stage.title}</p>
                        </div>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${styles.pill}`}>
                          {styles.pillText}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{stage.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* All-set CTA */}
            {allComplete && onNavigateToPhoneNumbers && (
              <div className="mt-2 pt-6 border-t border-gray-100">
                <button
                  onClick={onNavigateToPhoneNumbers}
                  className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 flex items-center justify-center gap-2 transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  Manage Phone Numbers
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Admin escape hatch */}
        {isGlobalAdmin && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => setShowAdvancedWizard(true)}
              className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1.5"
            >
              <SettingsIcon className="w-3.5 h-3.5" />
              Advanced: open manual registration wizard (admin)
            </button>
          </div>
        )}
      </div>
    );
  };

  if (checkingStatus) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-3 text-gray-600">Checking A2P registration status...</span>
      </div>
    );
  }

  // Default for everyone: read-only status. Only a global admin who explicitly
  // opts in sees the legacy manual wizard below.
  if (!isGlobalAdmin || !showAdvancedWizard) {
    return renderStatusView();
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Admin-only: this manual wizard is an escape hatch; registration is normally automated */}
      <div className="mb-6 flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5">
        <span className="text-xs text-gray-500 flex items-center gap-1.5">
          <SettingsIcon className="w-3.5 h-3.5" />
          Manual registration wizard (admin)
        </span>
        <button
          onClick={() => setShowAdvancedWizard(false)}
          className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1.5"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to status
        </button>
      </div>

      {renderStepIndicator()}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-green-800 text-sm">{success}</p>
        </div>
      )}

      {/* STEP 1: Business Profile + Authorized Rep */}
      {currentStep === 1 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <div className="flex items-center gap-3 mb-6">
            <Building2 className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Business Profile</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Tell us about your business and provide an authorized representative
          </p>

          <div className="space-y-6">
            {/* Business Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Legal Business Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.business_name}
                    onChange={(e) => updateField('business_name', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Acme Corporation"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.business_type}
                    onChange={(e) => updateField('business_type', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="LLC">LLC</option>
                    <option value="CORPORATION">Corporation</option>
                    <option value="SOLE_PROPRIETORSHIP">Sole Proprietorship</option>
                    <option value="NON_PROFIT">Non-Profit</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                {formData.business_type === 'SOLE_PROPRIETORSHIP' ? 'SSN' : 'EIN'}
                </label>
                  <input
                    type="text"
                    value={formatEINForDisplay(formData.business_registration_number, formData.business_type)}
                    onChange={(e) => updateField('business_registration_number', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={formData.business_type === 'SOLE_PROPRIETORSHIP' ? '123-45-6789' : '12-3456789'}
                    maxLength={formData.business_type === 'SOLE_PROPRIETORSHIP' ? 11 : 10}
                    />
                </div>

       <div>
  <label className="block text-sm font-medium text-gray-700 mb-2">
    Industry <span className="text-red-500">*</span>
  </label>
  <select
    value={formData.business_industry}
    onChange={(e) => updateField('business_industry', e.target.value)}
    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
  >
    <option value="">Select an industry</option>
    <option value="AGRICULTURE">Agriculture</option>
    <option value="AUTOMOTIVE">Automotive</option>
    <option value="BANKING">Banking</option>
    <option value="CONSTRUCTION">Construction</option>
    <option value="CONSUMER">Consumer</option>
    <option value="EDUCATION">Education</option>
    <option value="ELECTRONICS">Electronics</option>
    <option value="ENGINEERING">Engineering</option>
    <option value="ENERGY">Energy</option>
    <option value="FAST_MOVING_CONSUMER_GOODS">Fast Moving Consumer Goods</option>
    <option value="FINANCIAL">Financial</option>
    <option value="FINTECH">Fintech</option>
    <option value="FOOD_AND_BEVERAGE">Food & Beverage</option>
    <option value="GOVERNMENT">Government</option>
    <option value="HEALTHCARE">Healthcare</option>
    <option value="HOSPITALITY">Hospitality</option>
    <option value="INSURANCE">Insurance</option>
    <option value="JEWELRY">Jewelry</option>
    <option value="LEGAL">Legal</option>
    <option value="MANUFACTURING">Manufacturing</option>
    <option value="MEDIA">Media</option>
    <option value="NOT_FOR_PROFIT">Not For Profit</option>
    <option value="OIL_AND_GAS">Oil & Gas</option>
    <option value="ONLINE">Online</option>
    <option value="PROFESSIONAL_SERVICES">Professional Services</option>
    <option value="RAW_MATERIALS">Raw Materials</option>
    <option value="REAL_ESTATE">Real Estate</option>
    <option value="RELIGION">Religion</option>
    <option value="RETAIL">Retail</option>
    <option value="TECHNOLOGY">Technology</option>
    <option value="TELECOMMUNICATIONS">Telecommunications</option>
    <option value="TRANSPORTATION">Transportation</option>
    <option value="TRAVEL">Travel</option>
  </select>
</div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="contact@acme.com"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website <span className="text-red-500">*</span>
                    </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => updateField('website', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://www.acme.com"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.street}
                    onChange={(e) => updateField('street', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="123 Main Street"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => updateField('city', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Orlando"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => updateField('state', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="FL"
                    maxLength="2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Postal Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => updateField('postal_code', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="32801"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.country}
                    onChange={(e) => updateField('country', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Authorized Representative */}
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Authorized Representative</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.auth_rep_first_name}
                    onChange={(e) => updateField('auth_rep_first_name', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="John"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.auth_rep_last_name}
                    onChange={(e) => updateField('auth_rep_last_name', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.auth_rep_email}
                    onChange={(e) => updateField('auth_rep_email', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="john.doe@acme.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.auth_rep_phone}
                    onChange={(e) => updateField('auth_rep_phone', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.auth_rep_business_title}
                    onChange={(e) => updateField('auth_rep_business_title', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="CEO"
                  />
                </div>

                <div>
  <label className="block text-sm font-medium text-gray-700 mb-2">
    Job Position <span className="text-red-500">*</span>
  </label>
  <select
    value={formData.auth_rep_job_position}
    onChange={(e) => updateField('auth_rep_job_position', e.target.value)}
    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
  >
    <option value="Director">Director</option>
    <option value="GM">GM</option>
    <option value="VP">VP</option>
    <option value="CEO">CEO</option>
    <option value="CFO">CFO</option>
    <option value="General Counsel">General Counsel</option>
    <option value="Other">Other</option>
  </select>
</div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <button
              onClick={handleStep1Submit}
              disabled={!canProceedStep1() || loading}
              className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 ${
                canProceedStep1() && !loading
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit for Approval
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* STEP 2: Waiting for Customer Profile Approval */}
      {currentStep === 2 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <div className="flex items-center gap-3 mb-6">
  {registrationStatus.customerProfile.status === 'twilio-approved' ? (
    <>
      <CheckCircle className="w-6 h-6 text-green-600" />
      <h2 className="text-2xl font-bold text-gray-900">Ready to Register Brand</h2>
    </>
  ) : registrationStatus.customerProfile.status === 'twilio-rejected' ? (
    <>
      <AlertCircle className="w-6 h-6 text-red-600" />
      <h2 className="text-2xl font-bold text-gray-900">Profile Rejected</h2>
    </>
  ) : (
    <>
      <Clock className="w-6 h-6 text-yellow-600" />
      <h2 className="text-2xl font-bold text-gray-900">Awaiting Approval</h2>
    </>
  )}
</div>

          {registrationStatus.customerProfile.status === 'twilio-rejected' ? (
  <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
    <p className="text-red-800">
      <strong>Your business profile was rejected by Twilio.</strong> Please check your email for specific rejection reasons. 
      You'll need to fix the issues and resubmit the form.
    </p>
  </div>
) : registrationStatus.customerProfile.status === 'twilio-approved' ? (
  <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
    <p className="text-green-800">
      <strong>Your business profile has been approved!</strong> You can now register your A2P brand below.
    </p>
  </div>
) : (
  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
    <p className="text-yellow-800">
      Your business profile has been submitted to Twilio for review. This typically takes 5-10 business days.
      Once approved, you'll be able to register your A2P brand.
    </p>
  </div>
)}

          <div className="space-y-4">
  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
    <div>
      <p className="font-medium text-gray-900">Subaccount Created</p>
      <p className="text-sm text-gray-600">Twilio subaccount is active</p>
    </div>
  </div>

  <div className={`flex items-start gap-3 p-4 rounded-lg ${
    registrationStatus.customerProfile.status === 'twilio-rejected' ? 'bg-red-50' :
    registrationStatus.customerProfile.status === 'twilio-approved' ? 'bg-green-50' :
    'bg-yellow-50'
  }`}>
    {registrationStatus.customerProfile.status === 'twilio-rejected' ? (
      <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
    ) : registrationStatus.customerProfile.status === 'twilio-approved' ? (
      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
    ) : (
      <Clock className="w-5 h-5 text-yellow-600 mt-0.5" />
    )}
    <div>
      <p className="font-medium text-gray-900">
        {registrationStatus.customerProfile.status === 'twilio-rejected' ? 'Customer Profile Rejected' :
         registrationStatus.customerProfile.status === 'twilio-approved' ? 'Customer Profile Approved' :
         'Customer Profile Pending'}
      </p>
      <p className="text-sm text-gray-600">
        {registrationStatus.customerProfile.status === 'twilio-rejected' ? 'Check your email for rejection reasons' :
         registrationStatus.customerProfile.status === 'twilio-approved' ? 'Ready to register A2P Brand' :
         'Waiting for Twilio approval'}
      </p>
    </div>
  </div>
</div>

<div className="mt-8 flex gap-3">
            <button
              onClick={checkA2PStatus}
              disabled={checkingStatus}
              className="px-6 py-3 border border-gray-300 rounded-lg font-medium flex items-center gap-2 hover:bg-gray-50"
            >
              <RefreshCw className={`w-5 h-5 ${checkingStatus ? 'animate-spin' : ''}`} />
              Refresh Status
            </button>

            {registrationStatus.customerProfile.status === 'twilio-rejected' && (
              <button
                onClick={() => setCurrentStep(1)}
                className="px-6 py-3 bg-red-600 text-white rounded-lg font-medium flex items-center gap-2 hover:bg-red-700"
              >
                <ArrowLeft className="w-5 h-5" />
                Edit & Resubmit
              </button>
            )}

            {registrationStatus.customerProfile.status === 'twilio-approved' && (
              <button
                onClick={handleStep2Submit}
                disabled={loading}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium flex items-center gap-2 hover:bg-blue-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Creating Brand...
                  </>
                ) : (
                  <>
                    Register A2P Brand
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}

{/* STEP 2.5: Brand Waiting Screen */}
      {currentStep === 2.5 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <div className="flex items-center gap-3 mb-6">
            {registrationStatus.brand.status === 'VERIFIED' || registrationStatus.brand.status === 'APPROVED' ? (
              <>
                <CheckCircle className="w-6 h-6 text-green-600" />
                <h2 className="text-2xl font-bold text-gray-900">Brand Approved - Ready for Campaign</h2>
              </>
            ) : registrationStatus.brand.status === 'FAILED' || registrationStatus.brand.status === 'REJECTED' ? (
              <>
                <AlertCircle className="w-6 h-6 text-red-600" />
                <h2 className="text-2xl font-bold text-gray-900">Brand Rejected</h2>
              </>
            ) : (
              <>
                <Clock className="w-6 h-6 text-yellow-600" />
                <h2 className="text-2xl font-bold text-gray-900">Awaiting Brand Approval</h2>
              </>
            )}
          </div>

          {registrationStatus.brand.status === 'FAILED' || registrationStatus.brand.status === 'REJECTED' ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
              <p className="text-red-800">
                <strong>Your brand registration was rejected by The Campaign Registry (TCR).</strong> Please check your email for specific rejection reasons. 
                You may need to contact Twilio support for assistance.
              </p>
            </div>
          ) : registrationStatus.brand.status === 'VERIFIED' || registrationStatus.brand.status === 'APPROVED' ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
              <p className="text-green-800">
                <strong>Your brand has been approved by The Campaign Registry!</strong> You can now create an A2P campaign below.
              </p>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
              <p className="text-yellow-800">
                Your brand has been submitted to The Campaign Registry (TCR) for review. This typically takes 1-3 business days.
                Once approved, you'll be able to create your A2P campaign.
              </p>
            </div>
          )}

          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Subaccount Created</p>
                <p className="text-sm text-gray-600">Twilio subaccount is active</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Customer Profile Approved</p>
                <p className="text-sm text-gray-600">Business profile verified by Twilio</p>
              </div>
            </div>

            <div className={`flex items-start gap-3 p-4 rounded-lg ${
              registrationStatus.brand.status === 'FAILED' || registrationStatus.brand.status === 'REJECTED' ? 'bg-red-50' :
              registrationStatus.brand.status === 'VERIFIED' || registrationStatus.brand.status === 'APPROVED' ? 'bg-green-50' :
              'bg-yellow-50'
            }`}>
              {registrationStatus.brand.status === 'FAILED' || registrationStatus.brand.status === 'REJECTED' ? (
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              ) : registrationStatus.brand.status === 'VERIFIED' || registrationStatus.brand.status === 'APPROVED' ? (
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              ) : (
                <Clock className="w-5 h-5 text-yellow-600 mt-0.5" />
              )}
              <div>
                <p className="font-medium text-gray-900">
                  {registrationStatus.brand.status === 'FAILED' || registrationStatus.brand.status === 'REJECTED' ? 'Brand Rejected' :
                   registrationStatus.brand.status === 'VERIFIED' || registrationStatus.brand.status === 'APPROVED' ? 'Brand Approved' :
                   'Brand Pending Approval'}
                </p>
                <p className="text-sm text-gray-600">
                  {registrationStatus.brand.status === 'FAILED' || registrationStatus.brand.status === 'REJECTED' ? 'Contact Twilio support for assistance' :
                   registrationStatus.brand.status === 'VERIFIED' || registrationStatus.brand.status === 'APPROVED' ? 'Ready to create A2P Campaign' :
                   'Waiting for TCR approval (1-3 business days)'}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex gap-3">
            <button
              onClick={checkA2PStatus}
              disabled={checkingStatus}
              className="px-6 py-3 border border-gray-300 rounded-lg font-medium flex items-center gap-2 hover:bg-gray-50"
            >
              <RefreshCw className={`w-5 h-5 ${checkingStatus ? 'animate-spin' : ''}`} />
              Refresh Status
            </button>

            {(registrationStatus.brand.status === 'VERIFIED' || registrationStatus.brand.status === 'APPROVED') && (
              <button
                onClick={() => setCurrentStep(3)}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium flex items-center gap-2 hover:bg-blue-700"
              >
                Continue to Campaign Setup
                <ArrowRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      )}

{/* STEP 3: Campaign Setup */}
{currentStep === 3 && (
  <div className="bg-white rounded-xl border border-gray-200 p-8">
    {/* Header */}
    <div className="flex items-center gap-3 mb-6">
      <MessageSquare className="w-6 h-6 text-blue-600" />
      <h2 className="text-2xl font-bold text-gray-900">Campaign Setup</h2>
    </div>

    {/* Explainer Card */}
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <p className="text-blue-800 text-sm">
        <strong>What's happening:</strong> SurFox will automatically register your campaign with carriers for compliance. 
        We'll handle the technical details—you just need to provide a few basics.
      </p>
    </div>
    {/* Brand Status Check - Show if brand not approved yet */}
{registrationStatus.brand.status && !['APPROVED', 'VERIFIED'].includes(registrationStatus.brand.status) && (
  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
    <div className="flex items-center gap-3">
      <Clock className="w-5 h-5 text-yellow-600" />
      <div>
        <p className="font-medium text-yellow-800">Waiting for Brand Approval</p>
        <p className="text-sm text-yellow-700 mt-1">
          Your A2P Brand is currently <strong>{registrationStatus.brand.status}</strong>. 
          You can create your campaign once it's approved (usually 1-3 hours).
        </p>
      </div>
    </div>
  </div>
)}


    <div className="space-y-6">
      {/* 1️⃣ Campaign Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Campaign Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.campaign_description}
          onChange={(e) => updateField('campaign_description', e.target.value)}
          placeholder="e.g., Property Seller Re-Engagement"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <div className="flex justify-between items-center mt-1">
          <p className="text-xs text-gray-500">Examples: Property Seller Re-Engagement, Dental Clinic Follow-Up, Insurance Renewal Conversation</p>
          {!formData.campaign_description && (
  <button
    onClick={async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const tenantId = user?.app_metadata?.tenant_id;
      
      // Fetch company name from customer_profiles
      const { data: customerProfile } = await supabase
        .from('customer_profiles')
        .select('business_name')
        .eq('tenant_id', tenantId)
        .single();
      
      const companyName = customerProfile?.business_name || 'Sales';
      updateField('campaign_description', `${companyName} Outreach Campaign`);
    }}
    className="text-xs text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap"
  >
    Auto-generate campaign name →
  </button>
)}
        </div>
      </div>

      {/* 2️⃣ Campaign Use Case - Locked */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Campaign Use Case
        </label>
        <div className="px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-600 cursor-not-allowed">
          <div className="flex items-center justify-between">
            <div>
              <span className="font-medium text-gray-800">Conversational / Marketing (Mixed)</span>
              <p className="text-xs text-gray-600 mt-1">Ideal for lead nurturing, follow-ups, and conversational sales outreach</p>
            </div>
            <Shield className="w-5 h-5 text-green-600 flex-shrink-0" />
          </div>
        </div>
      </div>

      {/* 3️⃣ Estimated Daily Messages - Button Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Message Volume Tier <span className="text-red-500">*</span>
        </label>
        <div className="space-y-3">
          {/* Low Volume Standard - Default */}
          <button
            onClick={() => updateField('daily_message_volume', 'low_volume_standard')}
            className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
              (formData.daily_message_volume === 'low_volume_standard' || !formData.daily_message_volume)
                ? 'border-green-500 bg-green-50'
                : 'border-gray-300 hover:border-green-300'
            }`}
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl">🟢</span>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">Low Volume Standard</span>
                  {(formData.daily_message_volume === 'low_volume_standard' || !formData.daily_message_volume) && (
                    <span className="px-2 py-0.5 bg-green-600 text-white text-xs rounded-full">Recommended</span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">Up to 6,000 messages/day</p>
              </div>
            </div>
          </button>

          {/* Standard Volume */}
          <div className="relative">
            <button
              onClick={() => updateField('daily_message_volume', 'standard_volume')}
              className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                formData.daily_message_volume === 'standard_volume'
                  ? 'border-yellow-500 bg-yellow-50'
                  : 'border-gray-300 hover:border-yellow-300'
              }`}
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl">🟡</span>
                <div className="flex-1">
                  <span className="font-semibold text-gray-900">Standard Volume</span>
                  <p className="text-sm text-gray-600 mt-1">6,000+ messages/day (requires review)</p>
                </div>
              </div>
            </button>
            {formData.daily_message_volume === 'standard_volume' && (
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs text-yellow-800">
                  <strong>Note:</strong> Standard Volume campaigns require higher carrier throughput and may take longer to approve.
                </p>
              </div>
            )}
          </div>

          {/* High Volume - Disabled */}
          <button
            disabled
            className="w-full p-4 border-2 border-gray-200 rounded-lg text-left bg-gray-50 cursor-not-allowed opacity-60"
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl">🔵</span>
              <div className="flex-1">
                <span className="font-semibold text-gray-700">High / Custom</span>
                <p className="text-sm text-gray-500 mt-1">Enterprise scale — contact SurFox Support</p>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* 4️⃣ Opt-In Flow - Pre-populated with dropdown */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          How do leads opt-in? <span className="text-red-500">*</span>
        </label>
        <select
          value={formData.opt_in_preset || 'default'}
          onChange={(e) => {
            const preset = e.target.value;
            updateField('opt_in_preset', preset);
            
            const presets = {
              'default': 'Leads opt in by submitting a contact or inquiry form',
              'inbound': 'Leads opt in by texting the business first or submitting a form',
              'existing': 'Existing customers who gave their number and agreed to be contacted',
              'referral': 'Leads opt in at an event or through a referral signup form'
            };
            
            updateField('message_flow', presets[preset] || presets['default']);
          }}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="default">Web form submission (Recommended)</option>
          <option value="inbound">Inbound text or form submission</option>
          <option value="existing">Existing customer contact</option>
          <option value="referral">Referral or event signup</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">This describes how recipients consent to receive messages</p>
      </div>

      {/* 5️⃣ Sample Messages Modal */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Sample Messages <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-gray-600 mb-3">
          Examples are required for A2P approval. STOP/HELP instructions are automatically included.
        </p>

        {formData.message_samples.length === 0 ? (
  <button
    onClick={() => {
      const defaultSamples = [
        "Hi {first_name}, this is {company} following up on your inquiry. Do you have a couple minutes to chat? Reply STOP to opt out.",
        "Hi {first_name}, thanks for reaching out to {company}! Are you still interested? Happy to answer any questions. Reply STOP to unsubscribe.",
        "Hi {first_name}, just checking in from {company} — is now a good time to connect? Reply STOP to opt out."
      ];
      setFormData(prev => ({ ...prev, message_samples: defaultSamples }));
    }}
    className="w-full px-4 py-3 border-2 border-blue-300 bg-blue-50 rounded-lg text-blue-700 font-medium hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
  >
    <MessageSquare className="w-4 h-4" />
    Preview Example Messages
  </button>
        ) : (
          <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm font-medium text-gray-700">Example Messages</span>
              <button
                onClick={() => setFormData(prev => ({ ...prev, message_samples: [] }))}
                className="text-xs text-blue-600 hover:text-blue-700"
              >
                Change Examples
              </button>
            </div>
            <div className="space-y-3">
              {formData.message_samples.map((sample, index) => (
                <div key={index} className="p-3 bg-white border border-gray-200 rounded text-sm text-gray-700">
                  {sample}
                </div>
              ))}
              <div className="p-2 bg-gray-100 border border-gray-200 rounded text-xs text-gray-600 italic">
                STOP to opt-out | HELP for info
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 6️⃣ Compliance Acknowledgment */}
      <div className="border-t pt-6">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-blue-600" />
          <p className="text-sm font-semibold text-gray-800">Compliance Acknowledgment</p>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-xs text-blue-800">
            Your campaign will be automatically reviewed by carriers after submission.
          </p>
        </div>

        <div className="space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.compliance_opt_in || false}
              onChange={(e) => updateField('compliance_opt_in', e.target.checked)}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              I confirm all leads have opted in via compliant methods
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.compliance_opt_out || false}
              onChange={(e) => updateField('compliance_opt_out', e.target.checked)}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              I understand messages must include STOP/HELP instructions
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.compliance_tcpa || false}
              onChange={(e) => updateField('compliance_tcpa', e.target.checked)}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              I agree to comply with TCPA, CTIA, and carrier messaging guidelines
            </span>
          </label>
          <p className="text-xs text-gray-600 mt-3 pt-3 border-t border-gray-200">
            Your confirmations help ensure smooth A2P 10DLC approval and faster carrier verification.
          </p>
        </div>
      </div>

      {/* 7️⃣ Campaign Summary Card */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-5">
  <div className="flex justify-between items-start mb-4">
    <p className="text-sm font-semibold text-gray-800">Campaign Summary</p>
    <Shield className="w-5 h-5 text-blue-600" />
  </div>
  <div className="space-y-2.5 text-sm">
    <div className="flex justify-between">
      <span className="font-semibold text-gray-700">Brand Name:</span>
      <span className="font-medium text-gray-900">SurFox Ai Inc</span>
    </div>
    <div className="flex justify-between">
      <span className="font-semibold text-gray-700">Registration Type:</span>
      <span className="font-medium text-gray-900">
        {formData.daily_message_volume === 'standard_volume' ? 'Standard Volume' : 'Low Volume Standard'}
      </span>
    </div>
    <div className="flex justify-between">
      <span className="font-semibold text-gray-700">Review Time Estimate:</span>
      <span className="font-medium text-blue-700">1–3 hours average</span>
    </div>
    <div className="flex justify-between">
      <span className="font-semibold text-gray-700">Use Case:</span>
      <span className="font-medium text-gray-900">Conversational / Marketing (Mixed)</span>
    </div>
    <div className="flex justify-between">
      <span className="font-semibold text-gray-700">Opt-out Method:</span>
      <span className="font-medium text-gray-900">Reply STOP</span>
    </div>
  </div>
</div>
    </div>

    {/* 8️⃣ CTA / Button Behavior */}
    <div className="mt-8 flex justify-between">
      <button
        onClick={() => setCurrentStep(2)}
        className="px-6 py-3 border border-gray-300 rounded-lg font-medium flex items-center gap-2 hover:bg-gray-50"
      >
        <ArrowLeft className="w-5 h-5" />
        Back
      </button>
      
      <button
  onClick={handleStep3Submit}
  disabled={!canProceedStep3() || loading}
  className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-all ${
    canProceedStep3() && !loading
      ? 'bg-blue-600 text-white hover:bg-gradient-to-r hover:from-blue-600 hover:to-blue-700 hover:shadow-lg'
      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
  }`}
>
  {loading ? (
    <>
      <Loader2 className="w-5 h-5 animate-spin" />
      Submitting to carriers...
    </>
  ) : (
    <>
      Submit for Carrier Review
      <ArrowRight className="w-5 h-5" />
    </>
  )}
</button>
    </div>
  </div>
)}

      {/* STEP 4: Awaiting Campaign Approval */}
{currentStep === 4 && (
  <>
    {registrationStatus.campaign?.status === 'VERIFIED' ? (
      // Campaign is VERIFIED - Show success and navigation button
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <div className="flex items-center gap-3 mb-6">
          <CheckCircle className="w-6 h-6 text-green-600" />
          <h2 className="text-2xl font-bold text-gray-900">Campaign Approved! 🎉</h2>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
          <p className="text-green-800 mb-4">
            <strong>Excellent! Your campaign has been approved by the carriers.</strong>
          </p>
          <p className="text-green-800">
            You're now ready to select a phone number and start sending messages to your leads.
          </p>
        </div>

        <div className="space-y-4 mb-8">
          <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-gray-900">Business Profile Approved</p>
              <p className="text-sm text-gray-600">Your business information has been verified</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-gray-900">A2P Brand Registered</p>
              <p className="text-sm text-gray-600">Brand: {registrationStatus.brand?.sid}</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-gray-900">Campaign Approved ✓</p>
              <p className="text-sm text-gray-600">Campaign: {registrationStatus.campaign?.sid}</p>
              <p className="text-sm text-green-700 mt-2">Status: <strong>VERIFIED</strong></p>
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <button
            onClick={() => {
              if (onNavigateToPhoneNumbers) {
                onNavigateToPhoneNumbers();
              }
            }}
            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 flex items-center justify-center gap-2 transition-colors"
          >
            Continue to Phone Numbers
            <ArrowRight className="w-5 h-5" />
          </button>

          <button
            onClick={checkA2PStatus}
            disabled={checkingStatus}
            className="px-6 py-3 border border-gray-300 rounded-lg font-medium flex items-center gap-2 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${checkingStatus ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>
    ) : (
      // Campaign is still IN_PROGRESS or PENDING - Show waiting state
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <div className="flex items-center gap-3 mb-6">
          <Clock className="w-6 h-6 text-yellow-600" />
          <h2 className="text-2xl font-bold text-gray-900">Campaign Submitted - Awaiting Approval</h2>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
          <p className="text-yellow-800 mb-4">
            <strong>Your campaign has been submitted to carriers for review.</strong>
          </p>
          <p className="text-yellow-800">
            Approval typically takes <strong>1-3 business days</strong>. Once approved, you'll be able to select a phone number and start sending messages.
          </p>
        </div>

        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-gray-900">Business Profile Approved</p>
              <p className="text-sm text-gray-600">Your business information has been verified</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-gray-900">A2P Brand Registered</p>
              <p className="text-sm text-gray-600">Brand: {registrationStatus.brand?.sid}</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <Clock className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-gray-900">Campaign Pending Approval</p>
              <p className="text-sm text-gray-600">Campaign: {registrationStatus.campaign?.sid}</p>
              <p className="text-sm text-yellow-700 mt-2">
                Status: <strong>{registrationStatus.campaign?.status || 'IN_PROGRESS'}</strong>
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <button
            onClick={checkA2PStatus}
            disabled={checkingStatus}
            className="px-6 py-3 border border-gray-300 rounded-lg font-medium flex items-center gap-2 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${checkingStatus ? 'animate-spin' : ''}`} />
            Check Approval Status
          </button>
        </div>

        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>What happens next?</strong> Once your campaign is approved, you'll select a phone number to start messaging your leads. You'll be notified when approval is complete.
          </p>
        </div>
      </div>
    )}
  </>
)}
    </div>
  );
};

export default A2PRegistration;